import React from "react";

import { templates } from "@reactivated";

import { PageSkeleton } from "../components/PageSkeleton";
import { ProductCard } from "../components/ProductCard";

type ICategoryCard =
    templates.ProductCategoryListPageTemplate["category_cards"][number];

// The `category-card` token is intentionally emitted twice.
const CategoryCard = ({ card }: { card: ICategoryCard }): React.JSX.Element => (
    <a href={card.link} className="card category-card category-card">
        {card.image_url && (
            <img
                src={card.image_url}
                className="card__image category-card__image responsive-img"
                alt={card.title}
                loading="lazy"
                decoding="async"
            />
        )}
        <div className="card__content category-card__content">
            {card.title && (
                <div className="card__title category-card__title">
                    {card.title}
                </div>
            )}
            {card.description_html && (
                <div
                    className="card__description category-card__description"
                    dangerouslySetInnerHTML={{
                        __html: card.description_html,
                    }}
                />
            )}
        </div>
    </a>
);

// Mutable reactivated props (not Immutable<>): this template composes the
// in-tree SiteHeader chain via PageSkeleton, which is typed with mutable
// reactivated props (see PageSkeleton).
export const Template = (
    props: templates.ProductCategoryListPageTemplate,
): React.JSX.Element => {
    const {
        header_copy,
        by_sleep_position_url,
        side_nav,
        is_expanded,
        sub_category_groups,
        category_cards,
    } = props;

    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            <div className="l-full-width u-margin-bottom--xl">
                <div className="category-list u-full-width-title">
                    <div className="category-list-header l-capped-width u-margin-bottom--l">
                        <div className="category-list-header__copy">
                            {header_copy.map((item, i) =>
                                item.kind === "heading" ? (
                                    <a key={i} href={by_sleep_position_url}>
                                        <div className="category-list-header__title">
                                            {item.value}
                                        </div>
                                    </a>
                                ) : (
                                    <div
                                        key={i}
                                        className="category-list-header__description"
                                        dangerouslySetInnerHTML={{
                                            __html: item.value,
                                        }}
                                    />
                                ),
                            )}
                        </div>
                    </div>

                    <div className="l-capped-width">
                        <div className="l-side-nav category-list-side-nav">
                            <div className="category-list-side-nav__mobile-toggle accordion">
                                Shop All
                            </div>
                            <ul className="category-list-side-nav__list category-list-side-nav__list--primary accordion-target">
                                {side_nav.map((l2cat, i) => (
                                    <li
                                        className="category-list-side-nav__item"
                                        key={i}
                                    >
                                        <a
                                            className="category-list-side-nav__link"
                                            href={l2cat.url}
                                        >
                                            {l2cat.name}
                                        </a>
                                        {l2cat.children.length > 0 && (
                                            <ul className="category-list-side-nav__list category-list-side-nav__list--secondary">
                                                {l2cat.children.map(
                                                    (l3cat, j) => (
                                                        <li
                                                            key={j}
                                                            className="category-list-side-nav__item category-list-side-nav__item--secondary"
                                                        >
                                                            <a
                                                                className="category-list-side-nav__link category-list-side-nav__link--secondary"
                                                                href={l3cat.url}
                                                            >
                                                                {l3cat.name}
                                                            </a>
                                                        </li>
                                                    ),
                                                )}
                                            </ul>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {is_expanded ? (
                            <div className="category-list-content category-list-content--expanded">
                                {sub_category_groups.map((group, i) => (
                                    <React.Fragment key={i}>
                                        <div className="category-list-sub-category__title">
                                            {group.title}
                                        </div>
                                        <div className="category-list-sub-category__list u-flex-container">
                                            {group.hero_image_url && (
                                                <div
                                                    className="card product-card sub-category-card sub-category-card__hero-image"
                                                    style={{
                                                        backgroundImage: `url(${group.hero_image_url})`,
                                                    }}
                                                ></div>
                                            )}
                                            {group.products.map((product) => (
                                                <ProductCard
                                                    key={product.id}
                                                    product={product}
                                                    className="sub-category-card"
                                                    showPreTitle={false}
                                                    showAuxCopy={false}
                                                />
                                            ))}
                                        </div>
                                    </React.Fragment>
                                ))}
                            </div>
                        ) : (
                            <div className="l-side-nav-content category-list-content u-flex-container l-capped-width">
                                {category_cards.map((card, i) => (
                                    <CategoryCard key={i} card={card} />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </PageSkeleton>
    );
};
